import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ListFilter, Users, Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminOrgContext } from "@/hooks/useAdminOrgContext";
import { useShortlists } from "@/hooks/useShortlists";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";

export default function AdminShortlists() {
  const { orgs, orgId, setSelectedOrgId, isLoading: orgsLoading } = useAdminOrgContext();
  const { shortlists, isLoading, createShortlist } = useShortlists(orgId);
  const navigate = useNavigate();
  const [newName, setNewName] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createShortlist.mutateAsync(newName.trim());
      toast({ title: "Shortlist criada!" });
      setNewName("");
      setShowCreate(false);
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    }
  };

  if (orgsLoading) {
    return <div className="p-8 flex justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="p-8 max-w-6xl">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Shortlists</h1>
          <p className="text-muted-foreground text-sm mt-1">Organize talentos em listas</p>
        </div>
        <Button variant="hero" onClick={() => setShowCreate(true)} disabled={!orgId}><Plus className="w-5 h-5" />Nova Shortlist</Button>
      </motion.div>

      <div className="mb-6">
        <label className="text-sm font-medium text-foreground mb-2 block">Organização</label>
        <Select value={orgId ?? ""} onValueChange={setSelectedOrgId}>
          <SelectTrigger className="w-72">
            <SelectValue placeholder="Selecione uma organização" />
          </SelectTrigger>
          <SelectContent>
            {orgs.map((o) => (
              <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showCreate && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-2xl p-6 border border-border mb-6">
          <h3 className="font-heading font-semibold text-foreground mb-4">Nova Shortlist</h3>
          <div className="flex gap-3">
            <Input placeholder="Nome da shortlist" value={newName} onChange={(e) => setNewName(e.target.value)} />
            <Button variant="hero" onClick={handleCreate} disabled={!newName.trim()}>Criar</Button>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
          </div>
        </motion.div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {shortlists.map((list) => (
            <div key={list.id} className="bg-card rounded-2xl p-5 border border-border hover:border-primary/30 transition-colors cursor-pointer"
              onClick={() => navigate(`/admin/shortlists/${list.id}`)}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <ListFilter className="w-5 h-5 text-primary" />
                </div>
              </div>
              <h3 className="font-heading font-semibold text-foreground mb-1">{list.name}</h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Users className="w-4 h-4" />
                <span>{(list.shortlist_items as any[])?.length ?? 0} talentos</span>
              </div>
              <p className="text-xs text-muted-foreground">Criada em: {format(new Date(list.created_at), "dd/MM/yyyy")}</p>
            </div>
          ))}

          {shortlists.length === 0 && !showCreate && (
            <div className="col-span-full bg-muted/50 rounded-2xl p-12 text-center">
              <ListFilter className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-foreground mb-2">Nenhuma shortlist</h3>
              <Button variant="hero" onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" />Criar primeira</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
